import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Paper,
  Divider,
  Alert,
  Chip,
} from '@mui/material'
import {
  FactCheck as FactCheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material'
import type { InvoiceExtraction, Evidence } from '@/types/invoiceAnalyser'

interface EvidenceDialogProps {
  open: boolean
  onClose: () => void
  extraction: InvoiceExtraction | null
}

const FIELD_LABELS: {
  [K in keyof Evidence]: string
} = {
  supplier: 'Supplier',
  invoice_date: 'Invoice Date',
  invoice_number: 'Invoice Number',
  currency: 'Currency',
  total_net: 'Net Amount',
  total_gross: 'Gross Amount',
  vat_amount: 'VAT Amount',
  tax_free_amount: 'Tax-Free Amount',
  taxable_net_amount: 'Taxable Net Amount',
  project_name: 'Project Name',
  recipient: 'Recipient',
}

function EvidenceField({
  label,
  value,
  evidence,
}: {
  label: string
  value: string | null
  evidence: string | null
}) {
  const hasValue = value !== null && value !== ''
  const hasEvidence = evidence !== null && evidence !== ''

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: hasValue ? 'success.main' : 'divider',
        borderRadius: 1,
        bgcolor: hasValue ? 'success.50' : 'background.paper',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Chip
          label={label}
          size="small"
          color={hasValue ? 'success' : 'default'}
        />
        {hasValue && <Chip label="Found" size="small" color="success" variant="outlined" />}
        {!hasValue && <Chip label="Not Found" size="small" color="default" variant="outlined" />}
      </Stack>

      {hasValue && (
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
          {value}
        </Typography>
      )}

      {hasEvidence && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 500 }}>
            Evidence:
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              mt: 0.5,
              bgcolor: 'background.paper',
              fontSize: '0.75rem',
              fontFamily: 'monospace',
              maxHeight: 60,
              overflow: 'auto',
            }}
          >
            {evidence}
          </Paper>
        </Box>
      )}
    </Box>
  )
}

export function EvidenceDialog({ open, onClose, extraction }: EvidenceDialogProps) {
  if (!extraction) return null

  const { evidence, warnings, confidence } = extraction

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1}>
            <FactCheckIcon />
            <Typography variant="h6">Audit Trail</Typography>
            <Chip
              label={confidence.toUpperCase()}
              color={confidence === 'high' ? 'success' : confidence === 'medium' ? 'warning' : 'error'}
              size="small"
            />
          </Stack>
          <Button onClick={onClose} startIcon={<CloseIcon />}>
            Close
          </Button>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          {warnings && warnings.length > 0 && (
            <Alert severity="warning">
              <Typography variant="subtitle2" gutterBottom>
                Warnings ({warnings.length})
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0 }}>
                {warnings.map((warning, idx) => (
                  <Typography component="li" key={idx} variant="body2">
                    {warning}
                  </Typography>
                ))}
              </Box>
            </Alert>
          )}

          <Divider />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Extracted Fields with Evidence
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <EvidenceField
              label={FIELD_LABELS.supplier}
              value={extraction.supplier}
              evidence={evidence.supplier}
            />

            <EvidenceField
              label={FIELD_LABELS.invoice_number}
              value={extraction.invoice_number}
              evidence={evidence.invoice_number}
            />

            <EvidenceField
              label={FIELD_LABELS.invoice_date}
              value={extraction.invoice_date}
              evidence={evidence.invoice_date}
            />

            <EvidenceField
              label={FIELD_LABELS.currency}
              value={extraction.currency}
              evidence={evidence.currency}
            />

            <EvidenceField
              label={FIELD_LABELS.total_net}
              value={extraction.total_net?.toString() ?? null}
              evidence={evidence.total_net}
            />

            <EvidenceField
              label={FIELD_LABELS.vat_amount}
              value={extraction.vat_amount?.toString() ?? null}
              evidence={evidence.vat_amount}
            />

            <EvidenceField
              label={FIELD_LABELS.tax_free_amount}
              value={extraction.tax_free_amount?.toString() ?? null}
              evidence={evidence.tax_free_amount}
            />

            <EvidenceField
              label={FIELD_LABELS.taxable_net_amount}
              value={extraction.taxable_net_amount?.toString() ?? null}
              evidence={evidence.taxable_net_amount}
            />

            <EvidenceField
              label={FIELD_LABELS.total_gross}
              value={extraction.total_gross?.toString() ?? null}
              evidence={evidence.total_gross}
            />

            <EvidenceField
              label={FIELD_LABELS.recipient}
              value={extraction.recipient}
              evidence={evidence.recipient}
            />

            <EvidenceField
              label={FIELD_LABELS.project_name}
              value={extraction.project_name}
              evidence={evidence.project_name}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              VAT Information
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="body2">
                <strong>USt-ID:</strong> {extraction.ust_id || 'Not detected'}
              </Typography>
              {!extraction.ust_id && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  VAT ID was not found in the document. It may be populated from the supplier whitelist.
                </Alert>
              )}
            </Paper>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
